import { useState, useCallback, useMemo, useEffect } from 'react';

const createIndexArray = (n: number): number[] => n < 0 ? [] : Array.from({ length: n }, (_, index) => index);

const useArrayReorder = <T>(arr: T[]) => {
  const [order, setOrder] = useState(createIndexArray(arr.length));

  useEffect(() => {
    if (arr.length !== order.length) setOrder(createIndexArray(arr.length));
  }, [arr]);

  const reorder = useCallback((wasArr: number[], become: number): void => {
    setOrder((prev) => {
      const indicies = prev
        .map((n, i) => [i, n] as [number, number])
        .filter(([i, n]) => wasArr.includes(n))
        .map(([i, n]) => i);
      const minInd = Math.min(...indicies);
      if (minInd < become) become = Math.min(become + 1, arr.length);

      let updatedArray = [...prev];
      for (const movedItem of wasArr) {
        if (movedItem >= 0) {
          updatedArray.splice(become, 0, movedItem);
          updatedArray = updatedArray.filter((item, i) => !(i !== become && item === movedItem))
        }
      }
      return updatedArray;
    });
  }, [arr]);

  const reset = useCallback(() => {
    setOrder(createIndexArray(arr.length));
  }, [arr]);

  const data = useMemo(() => order.map(n => arr[n]).filter(m => m !== undefined), [arr, order]);

  return {
    data,
    reorder,
    reset,
  };
};

export type ArrayReorder = ReturnType<typeof useArrayReorder>;
export default useArrayReorder;
